import React, { useState, useRef, useEffect, useCallback } from 'react';

function CitySelector({ cities = {}, currentDomain }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Don't render until cities data is loaded
  if (!cities || Object.keys(cities).length === 0) {
    return null;
  }

  // Get current city label
  const currentCity = cities[currentDomain];
  const currentLabel = currentCity?.label || 'Select City';

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCitySelect = useCallback((domainId) => {
    const city = cities[domainId];
    if (city && city.subdomain) {
      // Get current URL and replace subdomain
      const currentUrl = new URL(window.location.href);
      const hostParts = currentUrl.hostname.split('.');

      // Replace the first part (subdomain) with the new city subdomain
      if (hostParts.length >= 2) {
        hostParts[0] = city.subdomain;
        currentUrl.hostname = hostParts.join('.');
        window.location.href = currentUrl.toString();
      }
    }
    setIsOpen(false);
  }, [cities]);

  // Sort cities alphabetically by label
  const sortedCities = Object.entries(cities).sort((a, b) =>
    a[1].label.localeCompare(b[1].label)
  );

  return (
    <div className="city-selector" ref={dropdownRef}>
      <button
        className={`city-selector-trigger ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className="city-selector-label">City</span>
        <span className="city-selector-value">{currentLabel}</span>
        <svg
          className={`city-selector-chevron ${isOpen ? 'open' : ''}`}
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
        >
          <path
            d="M3 4.5L6 7.5L9 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="city-selector-dropdown" role="listbox">
          {sortedCities.map(([domainId, city]) => (
            <button
              key={domainId}
              className={`city-selector-option ${domainId === currentDomain ? 'selected' : ''}`}
              onClick={() => handleCitySelect(domainId)}
              role="option"
              aria-selected={domainId === currentDomain}
            >
              {city.label}
              {domainId === currentDomain && (
                <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
                  <path d="M11.78 4.22a.75.75 0 0 1 0 1.06l-5.5 5.5a.75.75 0 0 1-1.06 0l-2.5-2.5a.75.75 0 1 1 1.06-1.06L5.75 9.19l4.97-4.97a.75.75 0 0 1 1.06 0Z"/>
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default CitySelector;

